import { toast } from "sonner";
import { captureHandledError } from "./sentry";
import { isStaleChunkError } from "./staleChunk";

/**
 * Global safety net for unhandled promise rejections and runtime errors that
 * escape React. Awaited failures from `invokeFunction` already show a precise
 * Retry toast — this handler covers fire-and-forget cases that slip past it.
 */

let installed = false;

const recentErrors = new Map<string, number>();
const DEDUPE_WINDOW_MS = 5_000;

export interface ErrorEntry {
  id: string;
  timestamp: number;
  source: "promise" | "runtime" | "render";
  name: string;
  message: string;
  stack?: string;
  functionName: string | null;
}

const MAX_ENTRIES = 50;
const errorBuffer: ErrorEntry[] = [];
const listeners = new Set<() => void>();
let cachedSnapshot: ErrorEntry[] = [];

function emit() {
  cachedSnapshot = errorBuffer.slice().reverse();
  listeners.forEach((l) => l());
}

export function getErrorBuffer(): ErrorEntry[] {
  return cachedSnapshot;
}

export function clearErrorBuffer(): void {
  errorBuffer.length = 0;
  emit();
}

export function subscribeErrorBuffer(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function pushEntry(entry: ErrorEntry) {
  errorBuffer.push(entry);
  if (errorBuffer.length > MAX_ENTRIES) errorBuffer.shift();
  emit();
}

interface MaybeFunctionsHttpError {
  context?: { functionName?: string };
  __handledByInvoke?: boolean;
}

function normaliseError(input: unknown): Error {
  if (input instanceof Error) return input;
  if (typeof input === "string") return new Error(input);
  try {
    return new Error(JSON.stringify(input));
  } catch {
    return new Error(String(input));
  }
}

function shouldSkip(err: Error): boolean {
  // Already toasted by invokeFunction
  if ((err as unknown as MaybeFunctionsHttpError).__handledByInvoke) return true;

  const msg = err.message || "";
  const name = err.name || "";

  if (name === "AbortError") return true;
  if (/ResizeObserver loop/i.test(msg)) return true;

  // Network-offline failures are noise; the browser/UI will reflect it elsewhere.
  if (typeof navigator !== "undefined" && navigator.onLine === false) {
    if (/Failed to fetch|NetworkError|Load failed/i.test(msg)) return true;
  }

  return false;
}

function isDuplicate(key: string): boolean {
  const now = Date.now();
  const last = recentErrors.get(key);
  if (last && now - last < DEDUPE_WINDOW_MS) return true;
  recentErrors.set(key, now);
  // Light cleanup so the map doesn't grow unbounded.
  if (recentErrors.size > 50) {
    for (const [k, t] of recentErrors) {
      if (now - t > DEDUPE_WINDOW_MS) recentErrors.delete(k);
    }
  }
  return false;
}

function detectFunctionName(err: Error): string | null {
  const ctx = (err as unknown as MaybeFunctionsHttpError).context;
  if (ctx?.functionName) return ctx.functionName;
  const match = err.message?.match(/Edge Function "?([\w-]+)"?/i);
  return match?.[1] ?? null;
}

const STALE_CHUNK_RELOAD_KEY = "lov:stale-chunk-reloaded-at";
const STALE_CHUNK_ATTEMPTS_KEY = "lov:stale-chunk-attempts";

function cacheBustReload(): void {
  if (typeof window === "undefined") return;
  const { pathname, search, hash } = window.location;
  const sep = search.includes("?") || search === "" ? (search ? "&" : "?") : "?";
  const next = `${pathname}${search}${sep}_v=${Date.now()}${hash}`;
  window.location.replace(next);
}

export function maybeRecoverFromStaleChunk(input: unknown): boolean {
  const err = normaliseError(input);
  if (!isStaleChunkError(err)) return false;
  if (typeof window === "undefined") return false;

  let attempts = 0;
  try {
    const last = Number(sessionStorage.getItem(STALE_CHUNK_RELOAD_KEY) || "0");
    attempts = Number(sessionStorage.getItem(STALE_CHUNK_ATTEMPTS_KEY) || "0");
    // Within the 30s guard, escalate instead of silently bailing.
    if (Date.now() - last < 30_000) {
      if (attempts >= 2) return false; // give up — let the toast show
      sessionStorage.setItem(STALE_CHUNK_ATTEMPTS_KEY, String(attempts + 1));
      console.warn("[globalErrorHandler] Stale chunk persists, cache-busting reload:", err.message);
      cacheBustReload();
      return true;
    }
    sessionStorage.setItem(STALE_CHUNK_RELOAD_KEY, String(Date.now()));
    sessionStorage.setItem(STALE_CHUNK_ATTEMPTS_KEY, "1");
  } catch {
    // sessionStorage unavailable — fall through and reload anyway.
  }
  console.warn("[globalErrorHandler] Stale chunk detected, reloading once:", err.message);
  window.location.reload();
  return true;
}

export function reportUnhandledError(input: unknown, source: "promise" | "runtime" | "render"): void {
  const err = normaliseError(input);
  console.error("[globalErrorHandler]", source, err);

  // Stale dynamic-import chunks happen after a deploy or dev-server restart.
  // Auto-reload once to pick up the new chunk hashes instead of showing an error.
  if (maybeRecoverFromStaleChunk(err)) return;

  const functionName = detectFunctionName(err);

  // Always record to buffer first — even filtered/deduped errors are useful for debugging.
  pushEntry({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    timestamp: Date.now(),
    source,
    name: err.name || "Error",
    message: err.message || "",
    stack: err.stack,
    functionName,
  });

  // Send to Sentry before our local skip/dedupe — Sentry has its own grouping
  // and we want accurate issue counts. beforeSend in sentry.ts filters pure noise.
  captureHandledError(err, { source, functionName });

  if (shouldSkip(err)) return;
  if (isDuplicate(err.message)) return;

  const title = functionName ? `${functionName} failed` : "Something went wrong";

  toast.error(title, {
    description: err.message || "An unexpected error occurred.",
    action: {
      label: "Reload",
      onClick: () => window.location.reload(),
    },
  });
}

export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;

  window.addEventListener("unhandledrejection", (event) => {
    reportUnhandledError(event.reason, "promise");
  });

  window.addEventListener("error", (event) => {
    reportUnhandledError(event.error ?? event.message, "runtime");
  });
}
